import { Star } from 'lucide-react'
import type { UserPresence } from '../types'

function generateColorFromId(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 70%, 50%)`
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function isVirtualAgent(user: UserPresence): boolean {
  return user.id.startsWith('agent-') || user.email?.includes('archforge.local') === true || user.email?.includes('agent@') === true
}

function presenceDotClasses(user: UserPresence): string {
  if (!isVirtualAgent(user) || !user.agentStatus) {
    return user.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'
  }
  switch (user.agentStatus) {
    case 'thinking':
      return 'bg-emerald-500 animate-pulse'
    case 'completed':
      return 'bg-emerald-500'
    case 'error':
      return 'bg-red-500'
    case 'composing':
      return 'bg-violet-500 animate-pulse'
    case 'idle':
    default:
      return 'bg-amber-400'
  }
}

function agentStatusPrefix(as: NonNullable<UserPresence['agentStatus']>): string {
  switch (as) {
    case 'thinking':
      return '🤔 '
    case 'completed':
      return '✓ '
    case 'error':
      return '⚠️ '
    case 'composing':
      return '📝 '
    case 'idle':
    default:
      return '⏸ '
  }
}

function agentStatusLabel(as: NonNullable<UserPresence['agentStatus']>): string {
  switch (as) {
    case 'thinking':
      return 'Trabalhando'
    case 'completed':
      return 'Etapa OK'
    case 'error':
      return 'Erro'
    case 'composing':
      return 'Gerando texto'
    case 'idle':
    default:
      return 'Ocioso'
  }
}

interface UserListProps {
  users: UserPresence[]
}

export default function UserList({ users }: UserListProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          Participantes ao vivo ({users.length})
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
        {users.length === 0 && (
          <div className="text-center text-xs text-gray-400 dark:text-gray-600 py-4">
            Nenhum participante ao vivo — aguardando sala colaborativa
          </div>
        )}
        {users.map((user) => {
          const agent = isVirtualAgent(user)
          const color = user.color || generateColorFromId(user.id)
          const dotCls = presenceDotClasses(user)

          const subtitleTone =
            agent && user.agentStatus === 'thinking'
              ? 'text-emerald-600 dark:text-emerald-400'
              : agent && user.agentStatus === 'completed'
                ? 'text-emerald-700 dark:text-emerald-400'
              : agent && user.agentStatus === 'error'
                ? 'text-red-600 dark:text-red-400'
              : agent
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-gray-500 dark:text-gray-400'

          return (
            <div
              key={user.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="relative shrink-0">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: agent ? color : color }}
                  title={user.name}
                >
                  {/* Agentes já têm initials via Star antes — keep star for familiarity */}
                  {agent ? <Star size={12} className="opacity-95" strokeWidth={2} /> : getInitials(user.name)}
                </div>
                <span
                  className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-900 ${dotCls}`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                  {user.name}
                  {agent && user.agentStatus ? (
                    <span className="ml-2 text-[9px] font-normal opacity-75">
                      {agentStatusLabel(user.agentStatus)}
                    </span>
                  ) : null}
                </div>
                {user.badge ? (
                  <div className={`text-[10px] truncate leading-snug ${subtitleTone}`}>
                    {agent && user.agentStatus ? agentStatusPrefix(user.agentStatus) : null}
                    {user.badge}
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
